<script lang="ts">
  import { onMount } from 'svelte';

  export let navigationLinks: any[] = [];
  export let enableHeaderDropdowns: boolean = true;
  export let dropdownDelay: string = "200";

  let _class = "";
  export { _class as class };

  let openDropdownIndex: number | null = null;
  let openSubDropdownIndex: number | null = null;
  let dropdownCloseTimeout: ReturnType<typeof setTimeout> | null = null;

  let isMobileView = false;
  let navLinksElement: HTMLElement;
  let navbarElement: HTMLElement;
  let logoElement: HTMLElement;
  let actionsElement: HTMLElement;
  let headerElement: HTMLElement;
  let currentFontSize: number | null = null;
  let isAdjustingFontSize = false;

  const browser = typeof window !== 'undefined';
  let currentPath = '';

  // Reactive viewport detection
  function updateViewport() {
    if (browser) {
      isMobileView = window.innerWidth < 992;
    }
  }

  $: if (browser) {
    currentPath = window.location.pathname;
    updateViewport();
  }

  // Dropdown event handlers
  function handleMouseEnter(idx: number) {
    if (!isMobileView) {
      if (dropdownCloseTimeout) {
        clearTimeout(dropdownCloseTimeout);
        dropdownCloseTimeout = null;
      }
      openDropdownIndex = idx;
    }
  }
  
  function handleMouseLeave() {
    if (!isMobileView) {
      const delay = parseInt(dropdownDelay) || 200;
      dropdownCloseTimeout = setTimeout(() => {
        openDropdownIndex = null;
        dropdownCloseTimeout = null;
      }, delay);
    }
  }
  
  function handleDropdownMouseEnter() {
    if (!isMobileView && dropdownCloseTimeout) {
      clearTimeout(dropdownCloseTimeout);
      dropdownCloseTimeout = null;
    }
  }
  
  function handleDropdownMouseLeave() {
    if (!isMobileView) {
      const delay = parseInt(dropdownDelay) || 200;
      dropdownCloseTimeout = setTimeout(() => {
        openDropdownIndex = null;
        dropdownCloseTimeout = null;
      }, delay);
    }
  }
  
  function handleSubMouseEnter(idx: number) {
    if (!isMobileView) openSubDropdownIndex = idx;
  }
  
  function handleSubMouseLeave() {
    if (!isMobileView) openSubDropdownIndex = null;
  }

  // Function to check if nav items are overflowing and adjust font size accordingly
  function adjustNavFontSize() {
    if (!browser || isMobileView || !navLinksElement || !navbarElement || !logoElement || !actionsElement || isAdjustingFontSize) {
      return;
    }
    
    isAdjustingFontSize = true;

    const computedStyle = getComputedStyle(navLinksElement.querySelector('.nav-item a') as HTMLElement);
    const originalFontSizeStr = computedStyle.getPropertyValue('--nav-link-font-size');
    const originalFontSize = parseFloat(originalFontSizeStr);
    
    if (isNaN(originalFontSize)) {
      isAdjustingFontSize = false;
      return;
    }
    
    if (currentFontSize === null) {
      currentFontSize = originalFontSize;
    }
    
    // Calculate available width for nav links
    const navbarWidth = navbarElement.offsetWidth;
    const logoWidth = logoElement.offsetWidth;
    const actionsWidth = actionsElement.offsetWidth;
    const paddingBuffer = 60;
    const availableWidth = navbarWidth - logoWidth - actionsWidth - paddingBuffer;
    
    // Get the original gap between nav items
    const originalGapStr = getComputedStyle(navLinksElement).getPropertyValue('--nav-link-gap');
    const originalGap = parseFloat(originalGapStr);
    const currentGapStr = navLinksElement.style.getPropertyValue('--adjusted-gap') || originalGapStr;
    const currentGap = parseFloat(currentGapStr) || originalGap;
    
    // Get total width of all nav items at current font size
    const navItemsWidth = Array.from(navLinksElement.querySelectorAll('.nav-item')).reduce((total, item) => {
      return total + (item as HTMLElement).offsetWidth;
    }, 0);
    
    // Calculate total width with gaps
    const totalGapWidth = (navLinksElement.querySelectorAll('.nav-item').length - 1) * currentGap;
    const totalNavWidth = navItemsWidth + totalGapWidth;
    
    const viewportWidth = window.innerWidth;
    
    // Check if nav items are overflowing or close to overflowing
    // More conservative threshold (95% of available space)
    if (totalNavWidth > availableWidth * 0.95) {
      // First try reducing the gap before reducing font size
      if (currentGap > 10) {
        const newGap = currentGap > 30 ? currentGap - 10 : currentGap - 5;
        navLinksElement.style.setProperty('--adjusted-gap', `${Math.max(newGap, 10)}px`);
      } else {
        // If gap is already at minimum, reduce font size
        // Scale reduction factor based on screen size and overflow amount
        let reductionFactor = 0.2; // Default reduction
        
        // For larger screens or more significant overflow, increase reduction factor
        if (viewportWidth > 1200) {
          reductionFactor = 0.5; // More aggressive for large screens
        } else if (viewportWidth > 992) {
          reductionFactor = 0.3; // Moderately aggressive for medium screens
        }
        
        // If we're severely overflowing, increase reduction further
        if (totalNavWidth > availableWidth * 1.1) {
          reductionFactor += 0.2;
        }
        
        let newFontSize = currentFontSize - reductionFactor;
        
        // Don't go below a minimum readable font size
        if (newFontSize < 10) {
          newFontSize = 10;
        }
        
        // Apply the new font size
        navLinksElement.style.setProperty('--adjusted-font-size', `${newFontSize}px`);
        currentFontSize = newFontSize;
      }
    } else if (totalNavWidth < availableWidth * 0.85) {
      // Only increase if we have plenty of space (using a conservative threshold)
      if (currentGap < originalGap) {
        // Increase gap first, up to original, but do it gradually
        const newGap = Math.min(currentGap + 3, originalGap);
        navLinksElement.style.setProperty('--adjusted-gap', `${newGap}px`);
      } else if (currentFontSize < originalFontSize) {
        // Then increase font size if needed, veery gradually
        let newFontSize = currentFontSize + 0.1;
        
        // Don't exceed the original font size
        if (newFontSize > originalFontSize) {
          newFontSize = originalFontSize;
        }
        
        // Apply the new font size
        navLinksElement.style.setProperty('--adjusted-font-size', `${newFontSize}px`);
        currentFontSize = newFontSize;
      }
    }
    
    isAdjustingFontSize = false;
  }
  
  $: if (browser && navLinksElement) {
    const navLinksRect = navLinksElement.getBoundingClientRect();
    const navLinksHeight = navLinksRect.height;
    document.documentElement.style.setProperty('--nav-links-height', `${navLinksHeight}px`);
  }

  $: if (browser && navbarElement) {
    const navbarRect = navbarElement.getBoundingClientRect();
    const navbarHeight = navbarRect.height;
    document.documentElement.style.setProperty('--nav-height', `${navbarHeight}px`);
  }

  $: if (browser && headerElement) {
    // Use a small delay to ensure the component is fully rendered
    setTimeout(() => {
      const headerRect = headerElement.getBoundingClientRect();
      const headerHeight = headerRect.height;
      document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    }, 10);
  }

  onMount(() => {
    // Setup resize handler for viewport detection
    const handleResize = () => {
      if (browser) {
        updateViewport();
        
        // Reset font size on mobile view
        if (isMobileView && navLinksElement) {
          navLinksElement.style.removeProperty('--adjusted-font-size');
          navLinksElement.style.removeProperty('--adjusted-gap');
          currentFontSize = null;
        } else {
          setTimeout(adjustNavFontSize, 100);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    
    // Initial viewport detection
    updateViewport();
    
    // Run font size adjustment after initial render
    setTimeout(adjustNavFontSize, 100);
    
    // Measure header height after initial render
    setTimeout(() => {
      if (headerElement) {
        const headerRect = headerElement.getBoundingClientRect();
        const headerHeight = headerRect.height;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      }
      if (navbarElement) {
        const navbarRect = navbarElement.getBoundingClientRect();
        const navbarHeight = navbarRect.height;
        document.documentElement.style.setProperty('--nav-height', `${navbarHeight}px`);
      }

      if (navLinksElement) {
        const navLinksRect = navLinksElement.getBoundingClientRect();
        const navLinksHeight = navLinksRect.height;
        document.documentElement.style.setProperty('--nav-links-height', `${navLinksHeight}px`);
      }
    }, 100);
    
    // Set up a ResizeObserver to detect when the navbar or its contents change size
    if (browser && window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        if (!isMobileView) {
          adjustNavFontSize();
        }
        // Update header height when any element resizes
        if (headerElement) {
          const headerRect = headerElement.getBoundingClientRect();
          const headerHeight = headerRect.height;
          document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
        }
        
        // Update navbar height when any element resizes
        if (navbarElement) {
          const navbarRect = navbarElement.getBoundingClientRect();
          const navbarHeight = navbarRect.height;
          document.documentElement.style.setProperty('--nav-height', `${navbarHeight}px`);
        }

        if (navLinksElement) {
          const navLinksRect = navLinksElement.getBoundingClientRect();
          const navLinksHeight = navLinksRect.height;
          document.documentElement.style.setProperty('--nav-links-height', `${navLinksHeight}px`);
        }
      });
      
      if (navbarElement) {
        resizeObserver.observe(navbarElement);
      }
      if (logoElement) {
        resizeObserver.observe(logoElement);
      }
      if (actionsElement) {
        resizeObserver.observe(actionsElement);
      }
      if (navLinksElement) {
        resizeObserver.observe(navLinksElement);
        navLinksElement.querySelectorAll('.nav-item').forEach(item => {
          resizeObserver.observe(item);
        });
      }
      if (headerElement) {
        resizeObserver.observe(headerElement);
      }
      
      return () => {
        resizeObserver.disconnect();
        window.removeEventListener('resize', handleResize);
      };
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  });
</script>

<header class={_class} bind:this={headerElement}>
  <nav class="navbar" bind:this={navbarElement}>
    <!-- Logo zone  -->
    <div class="navbar-logo-zone" bind:this={logoElement}>
      <slot name="logo-zone" />
    </div>

    <!-- Desktop nav links -->
    <ul class="navbar-links" bind:this={navLinksElement}>
      {#each navigationLinks as link, idx}
        <li class="nav-item"
            class:has-dropdown={enableHeaderDropdowns && link.children && link.children.length > 0}
            on:mouseenter={() => enableHeaderDropdowns && handleMouseEnter(idx)}
            on:mouseleave={() => enableHeaderDropdowns && handleMouseLeave()}
        >
          {#if link.isSubmenu}
            <!-- Submenu item -->
            <a href={link.href} class="nav-submenu-title">
              {link.text}
            </a>
          {:else}
            <!-- Regular navigation item -->
            <a 
              href={link.href} 
              class:active={browser && link.href === currentPath}
            target={link.target}
            rel={link.target === '_blank' ? "noopener noreferrer" : undefined}
            >
              {link.text}
            </a>
          {/if}
          {#if enableHeaderDropdowns && link.children && link.children.length > 0 && openDropdownIndex === idx}
            <ul class="header-dropdown-menu"
                on:mouseenter={handleDropdownMouseEnter}
                on:mouseleave={handleDropdownMouseLeave}
            >
              {#each link.children as child, cidx}
                <li class="header-dropdown-navitem"
                    on:mouseenter={() => handleSubMouseEnter(cidx)}
                    on:mouseleave={handleSubMouseLeave}
                >
                  {#if child.isSubmenu}
                    <!-- Submenu item  -->
                    <a href={child.href} class="dropdown-submenu-title">
                      {child.text}
                    </a>
                  {:else}
                    <!-- Regular navigation item -->
                    <a 
                      href={child.href} 
                      class:has-children={child.children && child.children.length > 0}
                    target={child.target}
                    rel={child.target === '_blank' ? "noopener noreferrer" : undefined}
                    >
                      {child.text}
                    </a>
                  {/if}
                  {#if child.children && child.children.length > 0 && openSubDropdownIndex === cidx}
                    <ul class="header-dropdown-menu header-dropdown-submenu">
                      {#each child.children as subchild}
                        <li class="header-dropdown-navitem">
                          {#if subchild.isSubmenu}
                            <!-- Submenu item  -->
                            <a href={subchild.href} class="dropdown-submenu-title">
                              {subchild.text}
                            </a>
                          {:else}
                            <!-- Regular navigation item -->
                            <a 
                              href={subchild.href}
                              target={subchild.target}
                              rel={subchild.target === '_blank' ? "noopener noreferrer" : undefined}
                            >
                              {subchild.text}
                            </a>
                          {/if}
                        </li>
                      {/each}
                    </ul>
                  {/if}
                </li>
              {/each}
            </ul>
          {/if}
        </li>
      {/each}
    </ul>

    <!-- Actions zone  -->
    <div class="navbar-actions-zone" bind:this={actionsElement}>
      <slot name="actions-zone" />
    </div>
  </nav>
</header>

<style lang="scss">
  header {
    width: 100%;
    position: relative;
    height: auto;
    background-color: var(--header-background-color);
    max-width: 1920px;
    margin: 0 auto;
    border-bottom: var(--header-border-bottom-thickness) solid var(--header-border-bottom-color);
    font-family: var(--liferay-font-family-sans-serif);
    padding: var(--header-padding) 0;
  }

  .navbar {
    width: 100%;
    margin: 0 auto;
    padding: 0 1rem;
    display: flex;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 50;
    background-color: var(--header-background-color);

    .navbar-logo-zone {
      display: flex;
      align-items: center;
    }

    .navbar-links {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      list-style: none;
      margin: 0;
      padding: 0;
      padding-left: 1rem;
      gap: var(--adjusted-gap, var(--nav-link-gap));
      flex-wrap: nowrap;
      
      @media screen and (max-width: 991px) {
        display: none !important;
      }

      .nav-item {
        position: relative;
        
        a {
          color: var(--nav-link-color);
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          transition: opacity 0.2s ease;
          font-size: var(--adjusted-font-size, var(--nav-link-font-size));
          font-weight: 600;
          line-height: 28px;
          position: relative;
          white-space: nowrap;

          &.active {
            color: var(--nav-link-active-color);
            text-decoration: underline;
            text-underline-offset: var(--nav-link-underline-offset);
            text-decoration-thickness: var(--nav-link-underline-thickness);
            text-decoration-color: var(--nav-link-underline-color);
          }

          &:hover {
            color: var(--nav-link-hover-color);
            opacity: 0.7;
          }

          &.has-children {
            padding-right: 1.2em;
          }
        }

        .nav-submenu-title {
          color: var(--nav-link-color);
          text-decoration: none;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          font-size: var(--adjusted-font-size, var(--nav-link-font-size));
          font-weight: 600;
          line-height: 28px;
          position: relative;
          white-space: nowrap;
        }
      }

      /* Header Dropdown Menu Styles */
      .header-dropdown-menu {
        position: absolute;
        top: calc(var(--header-height, 80px) - var(--nav-links-height, 60px) - var(--header-border-bottom-thickness, 1px) - 1px);
        left: 0;
        background: var(--dropdown-background-color, #fff);
        border: 1px solid var(--dropdown-border-color, #e5e5e5);
        box-shadow: var(--dropdown-shadow, 0 4px 6px rgba(0,0,0,0.1));
        padding: var(--dropdown-padding-vertical, 1rem) var(--dropdown-padding-horizontal, 1rem);
        min-width: var(--dropdown-min-width, 180px);
        max-width: var(--dropdown-max-width, 300px);
        z-index: 200;
        font-size: var(--dropdown-item-font-size, 14px);
        font-weight: var(--dropdown-item-font-weight, 400);
        list-style: none;
        margin: 0;
        border-radius: 4px;
      }

      .header-dropdown-submenu {
        left: 100%;
        top: 0;
        margin-left: 2px;
      }

      .header-dropdown-navitem {
        position: relative;
        
        a {
          display: block;
          padding: var(--dropdown-item-padding-vertical, 0.5rem) var(--dropdown-item-padding-horizontal, 1rem);
          color: var(--dropdown-item-color, #424242);
          text-decoration: none;
          white-space: nowrap;
          transition: all 0.2s ease;
          text-transform: none;
          letter-spacing: normal;
          font-size: var(--dropdown-item-font-size, 14px);
          font-weight: var(--dropdown-item-font-weight, 400);

          &:hover {
            color: var(--dropdown-item-hover-color, #202020);
            background: var(--dropdown-item-hover-bg-color, rgba(0,0,0,0.05));
          }
        }

        .dropdown-submenu-title {
          display: block;
          padding: var(--dropdown-item-padding-vertical, 0.5rem) var(--dropdown-item-padding-horizontal, 1rem);
          color: var(--dropdown-item-color, #424242);
          text-decoration: none;
          white-space: nowrap;
          text-transform: none;
          letter-spacing: normal;
          font-size: var(--dropdown-item-font-size, 14px);
          font-weight: var(--dropdown-item-font-weight, 400);
        }
      }
    }

    .navbar-actions-zone {
      display: flex;
      align-items: center;
      gap: 1rem;
      margin-left: auto;

      @media screen and (max-width: 991px) {
        gap: 0.5rem;
      }
    }
  }
</style>